import { FormHandles } from '@unform/core';
import React, { useCallback, useRef } from 'react';
import * as Yup from 'yup';

import { ModalHeader } from '../../components/ModalHeader';
import { InputText } from '../../components/InputText';
import { useModal } from '../../hooks/modal';
import { useToast } from '../../hooks/toast';

import { api } from '../../services/api';
import { getValidationErrors } from '../../utils/getValidationErrors';

import {
  Cancel,
  Container,
  Content,
  FooterRow,
  FormContainer,
  Info,
  Submit,
  InputTextArea,
} from './styles';

interface IFormProps {
  title: string;
  content: string;
  scheduled_for?: string;
}

const AddWarning: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();
  const { removeModal } = useModal();

  const handleSubmit = useCallback(
    async (data: IFormProps) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          title: Yup.string().required('Campo obrigatório'),
          content: Yup.string().required('Campo obrigatório'),
          scheduled_for: Yup.string()
            .optional()
            .test(
              'is-future',
              'A data agendada precisa ser no futuro',
              value => !value || new Date(value).getTime() > Date.now(),
            ),
        });

        await schema.validate(data, { abortEarly: false });

        const scheduledForDate = data.scheduled_for
          ? new Date(data.scheduled_for)
          : undefined;

        await api.post('news', {
          title: data.title,
          content: data.content,
          scheduled_for: scheduledForDate?.toISOString(),
        });

        addToast({
          type: 'success',
          title: scheduledForDate
            ? `Aviso agendado para ${scheduledForDate.toLocaleString('pt-BR')}`
            : 'Aviso criado com sucesso',
        });

        removeModal();
      } catch (err: any) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }

        addToast({
          type: 'error',
          title: 'Ocorreu um erro',
          description:
            'Tente novamente mais tarde. Caso o erro persista, entre em contato com a administração do sistema.',
        });
      }
    },
    [addToast],
  );

  return (
    <Container>
      <ModalHeader title="Novo aviso" />

      <Content>
        <FormContainer ref={formRef} onSubmit={handleSubmit}>
          <InputText
            label="Titulo"
            name="title"
            placeholder="Ex.: Área de cardio fechada"
          />
          <InputTextArea
            label="Conteúdo"
            name="content"
            rows={4}
            placeholder="Detalhe o aviso para os alunos"
          />
          <InputText
            label="Agendar publicação (opcional)"
            name="scheduled_for"
            type="datetime-local"
          />
          <Info text="Deixe em branco para publicar imediatamente. Se agendado, o aviso só aparece para os alunos e a notificação só é enviada na data/hora escolhida." />

          <FooterRow>
            <Cancel type="button" onClick={() => removeModal()}>
              Cancelar
            </Cancel>
            <Submit name="submit" value="Publicar aviso" />
          </FooterRow>
        </FormContainer>
      </Content>
    </Container>
  );
};

export { AddWarning };
